import { useState, useMemo, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ArrowLeft, Send, CheckCircle2, XCircle, Lock, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  getConsentedUsers,
  sendBulkSms,
  type SmsFilter,
  type UserPreview,
} from "@/requests/internal-sms/sms-notifications";
import { SMS_MAX_MESSAGE_LENGTH, applyFilter } from "@shared/sms-filters";
import { formatPhoneNumber } from "@shared/address-validation";

export default function SmsNotificationsConfirmPage() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const filterParam = params.get("filter");

  const [message] = useState(
    () => params.get("message") ?? "",
  );
  const [filter] = useState<SmsFilter>(
    () =>
      filterParam && ["all", "post-pilot", "internal"].includes(filterParam)
        ? (filterParam as SmsFilter)
        : "all",
  );
  const [confirmationPassword, setConfirmationPassword] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data: previewData, isLoading: isLoadingPreview } = useQuery({
    queryKey: ["sms-consented-users"],
    queryFn: getConsentedUsers,
  });

  const filteredUsers = useMemo(
    () => (previewData ? applyFilter(previewData.users, filter) : []),
    [previewData, filter],
  );

  const sendMutation = useMutation({
    mutationFn: () =>
      sendBulkSms(message, filteredUsers, confirmationPassword),
  });

  useEffect(() => {
    if (!sendMutation.isPending) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [sendMutation.isPending]);

  const handleBack = () => {
    if (sendMutation.isPending) return;
    setLocation("/internal/sms-notifications");
  };

  const handleSendClick = () => {
    if (!confirmationPassword.trim()) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = () => {
    setShowConfirmDialog(false);
    sendMutation.mutate();
  };

  const formatName = (user: UserPreview) => {
    const parts = [user.firstName, user.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Unknown";
  };

  if (sendMutation.isSuccess && sendMutation.data) {
    const { sent, failed, succeeded, failedRecipients } = sendMutation.data;
    return (
      <Layout headerContent={
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Internal</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">SMS Confirm</span>
        </div>
      }>
        <div className="container mx-auto py-6 max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {failed === 0 ? (
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircle className="h-6 w-6 text-yellow-500" />
                )}
                SMS Send Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-lg font-semibold text-green-600">
                  Successfully sent: {sent}
                </p>
                {failed > 0 && (
                  <p className="text-lg font-semibold text-yellow-600">
                    Failed: {failed}
                  </p>
                )}
              </div>

              {succeeded.length > 0 && (
                <div className="space-y-2">
                  <p className="font-semibold text-sm">Delivered</p>
                  <ScrollArea className="h-40 w-full rounded border p-2">
                    <ul className="space-y-1 text-sm">
                      {succeeded.map((r) => (
                        <li key={r.userId}>{r.label}</li>
                      ))}
                    </ul>
                  </ScrollArea>
                </div>
              )}

              {failedRecipients.length > 0 && (
                <div className="space-y-2">
                  <p className="font-semibold text-sm">Not delivered</p>
                  <ScrollArea className="h-40 w-full rounded border p-2">
                    <ul className="space-y-1 text-sm">
                      {failedRecipients.map((r) => (
                        <li key={r.userId} className="text-destructive">
                          {r.label} — {r.error}
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </div>
              )}

              <Button onClick={handleBack} className="w-full">
                Back to SMS Notifications
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Internal</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">SMS Confirm</span>
      </div>
    }>
      <div className="container mx-auto py-6 max-w-4xl">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={sendMutation.isPending}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">Confirm SMS Notification</h1>
          <p className="text-muted-foreground mt-2">
            Review recipients and message before sending
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Message</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-sm bg-muted p-4 rounded">
                {message || "No message"}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {message.length} / {SMS_MAX_MESSAGE_LENGTH} characters
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recipients ({filteredUsers.length})</CardTitle>
              <CardDescription>
                {filter === "all" && "All users who have consented"}
                {filter === "post-pilot" && "Post-pilot users"}
                {filter === "internal" && "Internal users (@onsemble.com)"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingPreview ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading recipients...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No recipients found
                </div>
              ) : (
                <ScrollArea className="h-[400px] w-full">
                  <div className="space-y-2">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.userId}
                        className="border rounded p-3 text-sm space-y-1"
                      >
                        <div className="font-medium">{formatName(user)}</div>
                        <div className="text-muted-foreground">{user.email}</div>
                        <div className="text-muted-foreground">{formatPhoneNumber(user.phone)}</div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Security Confirmation
            </CardTitle>
            <CardDescription>
              Enter the confirmation password to send bulk SMS notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="confirmation-password">Confirmation Password</Label>
              <Input
                id="confirmation-password"
                type="password"
                placeholder="Enter confirmation password"
                value={confirmationPassword}
                onChange={(e) => setConfirmationPassword(e.target.value)}
                disabled={sendMutation.isPending}
                className="font-mono"
              />
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex gap-3">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={sendMutation.isPending}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendClick}
            disabled={sendMutation.isPending || !filteredUsers.length || !confirmationPassword.trim()}
            className="flex-1"
          >
            {sendMutation.isPending ? (
              "Sending..."
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send SMS to {filteredUsers.length} users
              </>
            )}
          </Button>
        </div>

        {sendMutation.isError && (
          <div className="mt-4 text-sm text-destructive">
            {sendMutation.error instanceof Error 
              ? sendMutation.error.message 
              : "Failed to send SMS. Please try again."}
          </div>
        )}

        {sendMutation.isPending && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 p-4 backdrop-blur-sm"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Card className="max-w-md w-full shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                  Sending SMS…
                </CardTitle>
                <CardDescription className="text-base text-foreground/90">
                  Delivering to {filteredUsers.length} recipient
                  {filteredUsers.length === 1 ? "" : "s"}. This usually takes
                  about a minute for larger lists and can run longer if sending
                  is slowed down.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Please keep this tab open until you see the results screen.
                  Closing or navigating away may cancel the send in your
                  browser.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk SMS Send</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to send an SMS to{" "}
              <span className="font-semibold text-foreground">
                {filteredUsers.length} users
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSend}>
              Yes, Send Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
